import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Store, Package, TrendingUp } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/format';

export const Route = createFileRoute('/_authed/marketplace/')({
  component: MarketplacePage,
});

interface MarketplaceDto {
  itemsSold: number;
  commissions: number;
  topItems: Array<{ id: string; name: string; sales: number; commission: number }>;
}

const mock: MarketplaceDto = {
  itemsSold: 1287,
  commissions: 14_320,
  topItems: Array.from({ length: 6 }, (_, i) => ({
    id: `it_${i}`,
    name: ['Tema Aurora', 'Plugin SEO Pro', 'Bloque FAQ', 'Integración Mailchimp', 'Tema Indigo', 'Plugin Analytics'][i],
    sales: 200 - i * 25,
    commission: 80 - i * 10,
  })),
};

function MarketplacePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace'],
    queryFn: async () => {
      try {
        return await api.get<MarketplaceDto>('/admin/marketplace');
      } catch {
        return mock;
      }
    },
  });
  const d = data ?? mock;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Marketplace</h1>
        <p className="text-muted-foreground">
          Items vendidos y comisiones de la plataforma
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-muted-foreground">Items vendidos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-bold">{d.itemsSold}</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-muted-foreground">Comisiones</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-bold">{formatCurrency(d.commissions)}</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-muted-foreground">Items listados</CardTitle>
            <Store className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-bold">58</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top items</CardTitle>
          <CardDescription>Los más vendidos este mes</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {d.topItems.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.sales} ventas
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="warning">
                    {formatCurrency(item.commission)} comisión
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
